import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "react-i18next";
import { trackPageView, trackEnrollmentClick } from "./analytics";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./components/ui/carousel";
import { ImageWithFallback } from "./components/figma/ImageWithFallback";
import { ChevronUp, Menu, X } from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
const labImage1 = "/assets/meristemo.jpeg";
const labImage2 = "/assets/4f21c0609349d1bcd214c081325ab2edbe7a6597.png";
const labImage3 = "/assets/b93eda330bcd12479a4b8aeeca5186042c9f7384.png";
import { CursoOnlinePage } from "./components/CursoOnlinePage";
import { CursoAvanzadoPage } from "./components/CursoAvanzadoPage";
import { PresencialPage } from "./components/PresencialPage";
import { WhitepaperPage } from "./components/WhitepaperPage";
import { WhitepaperThankYouPage } from "./components/WhitepaperThankYouPage";
import { BreedersPage } from "./components/BreedersPage";
import { LicensePage } from "./components/LicensePage";
import { AutodidactasPage } from "./components/AutodidactasPage";
import { StartupPage } from "./components/StartupPage";
import { NuestrosCursosPage } from "./components/NuestrosCursosPage";
import { CheckoutPage } from "./components/CheckoutPage";
import { PaymentConfirmationPage } from "./components/PaymentConfirmationPage";
import { LanguageSwitcher } from "./components/LanguageSwitcher";
import {
  generateCourseSchemaBroadcast,
  generateCourseSchemaAdvanced,
  generateOrganizationSchema
} from "./config/schema";

export default function App() {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState<'home' | 'curso-online' | 'curso-avanzado' | 'presencial' | 'whitepaper' | 'whitepaper-thankyou' | 'breeders' | 'license' | 'autodidactas' | 'startup' | 'nuestros-cursos' | 'checkout' | 'payment-confirmation'>('home');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [checkoutData, setCheckoutData] = useState<{ courseId?: string; orderData?: any }>({});

  // Testimonials data
  const testimonials = [
    {
      text: t("testimonials.testimonial1"),
      image: "/assets/entretenimiento.png",
      instagram: "@micultivomientretenimiento3.0"
    },
    {
      text: t("testimonials.testimonial2"),
      image: "/assets/hortilive.png",
      instagram: "@hortlilive"
    },
    {
      text: t("testimonials.testimonial3"),
      image: "/assets/universal.png",
      instagram: "@universalgrow"
    }
  ];

  // Auto-rotate testimonials every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "100%"],
  );
  const textY = useTransform(
    scrollYProgress,
    [0, 1],
    ["-20%", "200%"],
  );

  // Control scroll to top button and navbar visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 500);
      setShowNavbar(scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    if (sectionId === 'curso-online' && currentPage === 'home') {
      setCurrentPage('curso-online');
      setMobileMenuOpen(false);
      return;
    }
    
    if (sectionId === 'breeders' && currentPage === 'home') {
      setCurrentPage('breeders');
      setMobileMenuOpen(false);
      return;
    }
    
    if (sectionId === 'license' && currentPage === 'home') {
      setCurrentPage('license');
      setMobileMenuOpen(false);
      return;
    }
    
    if (sectionId === 'autodidactas' && currentPage === 'home') {
      setCurrentPage('autodidactas');
      setMobileMenuOpen(false);
      return;
    }
    
    if (sectionId === 'startup' && currentPage === 'home') {
      setCurrentPage('startup');
      setMobileMenuOpen(false);
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setMobileMenuOpen(false);
    }
  };

  // Navigate to curso online page
  const navigateToCursoOnline = () => {
    setCurrentPage('curso-online');
    trackPageView('curso-online', 'Curso Online Introducción');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to curso avanzado page
  const navigateToCursoAvanzado = () => {
    setCurrentPage('curso-avanzado');
    trackPageView('curso-avanzado', 'Curso Avanzado');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to presencial page
  const navigateToPresencial = () => {
    setCurrentPage('presencial');
    trackPageView('presencial', 'Masterclass Presencial');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to whitepaper page
  const navigateToWhitepaper = () => {
    setCurrentPage('whitepaper');
    trackPageView('whitepaper', 'Whitepaper Gratuito');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to whitepaper thank you page
  const navigateToWhitepaperThankYou = () => {
    setCurrentPage('whitepaper-thankyou');
    trackPageView('whitepaper-thankyou', 'Whitepaper Thank You');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to license page
  const navigateToLicense = () => {
    setCurrentPage('license');
    trackPageView('license', 'Licencias y Uso Comercial');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to nuestros cursos page
  const navigateToNuestrosCursos = () => {
    setCurrentPage('nuestros-cursos');
    trackPageView('nuestros-cursos', 'Nuestros Cursos');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to nuestros cursos page and scroll to instructor section
  const navigateToNuestrosCursosInstructor = () => {
    setCurrentPage('nuestros-cursos');
    // Wait for the page to render, then scroll to instructor section
    setTimeout(() => {
      const instructorSection = document.getElementById('profesor');
      if (instructorSection) {
        instructorSection.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  // Navigate to checkout page
  const navigateToCheckout = (courseId: string) => {
    const courseNames: Record<string, { name: string; price: number }> = {
      'online-introduccion': { name: 'ICIO - Curso Online Introducción', price: 299 },
      'online-avanzado': { name: 'ACIO - Curso Online Avanzado', price: 599 },
      'presencial': { name: 'Masterclass Presencial', price: 1499 },
      'combo': { name: 'Combo ICIO + ACIO', price: 799 },
    };
    const course = courseNames[courseId] || { name: courseId, price: 0 };
    trackEnrollmentClick(courseId, course.name, course.price);
    setCheckoutData({ courseId });
    setCurrentPage('checkout');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate to payment confirmation page
  const navigateToPaymentConfirmation = (orderData: any) => {
    setCheckoutData({ orderData });
    setCurrentPage('payment-confirmation');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Navigate back to home
  const navigateToHome = () => {
    setCurrentPage('home');
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navItems = [
    { id: "inicio", label: t("navigation.home") },
    { id: "curso-online", label: t("navigation.courses") },
    { id: "sobre-nosotros", label: t("navigation.about") },
    { id: "masterclass-avanzada", label: t("navigation.advanced") },
    { id: "presencial", label: t("navigation.presencial") },
    { id: "contacto", label: t("navigation.contact") },
  ];

  // Render curso online page
  if (currentPage === 'curso-online') {
    return <CursoOnlinePage onNavigateBack={navigateToHome} onNavigateToCheckout={navigateToCheckout} onNavigateToNuestrosCursos={navigateToNuestrosCursosInstructor} />;
  }

  // Render curso avanzado page
  if (currentPage === 'curso-avanzado') {
    return <CursoAvanzadoPage onNavigateBack={navigateToHome} onNavigateToCheckout={navigateToCheckout} onNavigateToNuestrosCursos={navigateToNuestrosCursosInstructor} />;
  }

  // Render presencial page
  if (currentPage === 'presencial') {
    return (
      <PresencialPage
        onNavigateBack={navigateToHome}
        onNavigateToCheckout={navigateToCheckout}
        onNavigateToCursoOnline={navigateToCursoOnline}
        onNavigateToCursoAvanzado={navigateToCursoAvanzado}
        onNavigateToNuestrosCursos={navigateToNuestrosCursosInstructor}
      />
    );
  }

  // Render whitepaper page
  if (currentPage === 'whitepaper') {
    return <WhitepaperPage onNavigateBack={navigateToHome} onNavigateToThankYou={navigateToWhitepaperThankYou} />;
  }

  // Render whitepaper thank you page
  if (currentPage === 'whitepaper-thankyou') {
    return <WhitepaperThankYouPage onNavigateBack={navigateToWhitepaper} onNavigateToHome={navigateToHome} />;
  }

  // Render breeders page
  if (currentPage === 'breeders') {
    return <BreedersPage onNavigateBack={navigateToHome} onNavigateToCursoOnline={navigateToCursoOnline} onNavigateToCursoAvanzado={navigateToCursoAvanzado} onNavigateToPresencial={navigateToPresencial} onNavigateToCheckout={navigateToCheckout} />;
  }

  // Render license page
  if (currentPage === 'license') {
    return (
      <LicensePage
        onNavigateBack={navigateToHome}
        onNavigateToCursoOnline={navigateToCursoOnline}
        onNavigateToCursoAvanzado={navigateToCursoAvanzado}
        onNavigateToPresencial={navigateToPresencial}
      />
    );
  }

  // Render autodidactas page
  if (currentPage === 'autodidactas') {
    return (
      <AutodidactasPage
        onNavigateBack={navigateToHome}
        onNavigateToCursoOnline={navigateToCursoOnline}
        onNavigateToCursoAvanzado={navigateToCursoAvanzado}
        onNavigateToPresencial={navigateToPresencial}
      />
    );
  }

  // Render startup page
  if (currentPage === 'startup') {
    return (
      <StartupPage
        onNavigateBack={navigateToHome}
        onNavigateToCursoOnline={navigateToCursoOnline}
        onNavigateToCursoAvanzado={navigateToCursoAvanzado}
        onNavigateToPresencial={navigateToPresencial}
      />
    );
  }

  // Render nuestros cursos page
  if (currentPage === 'nuestros-cursos') {
    return <NuestrosCursosPage onNavigateBack={navigateToHome} onNavigateToCursoOnline={navigateToCursoOnline} onNavigateToCursoAvanzado={navigateToCursoAvanzado} onNavigateToPresencial={navigateToPresencial} onNavigateToNuestrosCursos={navigateToNuestrosCursos} />;
  }

  // Render checkout page
  if (currentPage === 'checkout') {
    return <CheckoutPage onNavigateBack={navigateToHome} onNavigateToPaymentConfirmation={navigateToPaymentConfirmation} preselectedCourse={checkoutData.courseId} />;
  }

  // Render payment confirmation page
  if (currentPage === 'payment-confirmation') {
    return <PaymentConfirmationPage onNavigateBack={navigateToHome} onNavigateToHome={navigateToHome} orderData={checkoutData.orderData} />;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: showNavbar ? 0 : -100,
          opacity: showNavbar ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-orange-500/30"
        style={{ pointerEvents: showNavbar ? "auto" : "none" }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => scrollToSection("inicio")}
            >
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-8 h-8 mr-2"
              />
              <span className="text-white text-lg">
                {t("app.brand")}
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                >
                  {item.label}
                </button>
              ))}
              <LanguageSwitcher />
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden bg-black/95 border-t border-orange-500/30"
            >
              <div className="py-4 space-y-4">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-2 text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="px-4 pb-4">
                <LanguageSwitcher size="sm" />
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: showScrollTop ? 1 : 0,
          scale: showScrollTop ? 1 : 0.8,
        }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 z-50 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-colors duration-200"
        style={{
          pointerEvents: showScrollTop ? "auto" : "none",
        }}
      >
        <ChevronUp size={24} />
      </motion.button>

      {/* Hero Section */}
      <section
        id="inicio"
        className="relative h-screen overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 flex items-center justify-center pt-16"
      >
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="/assets/laboratorio vegetal.png"
            alt="Biotechnology Laboratory"
            className="w-full h-full object-cover opacity-20"
          />
        </motion.div>

        <motion.div
          style={{ y: textY }}
          className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto -mt-32"
        >
          <motion.img
            src={rebelLogo}
            alt="Rebel Genomics"
            className="w-48 h-48 mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />

          <motion.h1
            className="text-6xl md:text-8xl mb-8 tracking-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {t("hero.title")}
            <br />
            <span className="text-black">{t("hero.subtitle")}</span>
            <br />
            <span className="text-3xl md:text-5xl text-yellow-200">
              {t("hero.tagline")}
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            {t("hero.description")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
          >
            <div className="flex flex-col sm:flex-row gap-6 items-center justify-center relative -top-6">
              <Button
                size="lg"
                className="bg-black hover:bg-gray-800 text-white text-xl px-12 py-6 rounded-full text-center w-auto leading-tight"
                onClick={navigateToNuestrosCursos}
              >
                {t("navigation.courses")}
              </Button>
              
              <Button
                size="lg"
                className="bg-black hover:bg-gray-800 text-white text-xl px-12 py-6 rounded-full text-center w-auto leading-tight"
                onClick={navigateToWhitepaper}
              >
                {t("cta.download")}
              </Button>
            </div>
          </motion.div>


        </motion.div>
      </section>

      {/* 1. Sobre Nosotros Section - PRIMER LUGAR EN EL NUEVO ORDEN */}
      <section
        id="sobre-nosotros"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Carrusel de imágenes */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Carousel className="w-full max-w-lg mx-auto">
                <CarouselContent>
                  <CarouselItem>
                    <Card className="bg-white border-orange-500/20 shadow-2xl rounded-3xl overflow-hidden">
                      <img
                        src={labImage1}
                        alt={t("about-us.images.instructor")}
                        className="w-full h-96 object-cover"
                      />
                    </Card>
                  </CarouselItem>
                  <CarouselItem>
                    <Card className="bg-white border-orange-500/20 shadow-2xl rounded-3xl overflow-hidden">
                      <img
                        src={labImage2}
                        alt={t("about-us.images.practical")}
                        className="w-full h-96 object-cover"
                      />
                    </Card>
                  </CarouselItem>
                  <CarouselItem>
                    <Card className="bg-white border-orange-500/20 shadow-2xl rounded-3xl overflow-hidden">
                      <img
                        src={labImage3}
                        alt={t("about-us.images.professional")}
                        className="w-full h-96 object-cover"
                      />
                    </Card>
                  </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="bg-orange-500 border-orange-500 text-white hover:bg-orange-600" />
                <CarouselNext className="bg-orange-500 border-orange-500 text-white hover:bg-orange-600" />
              </Carousel>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              {t("about-us.hero.title")}
                <br />
                <span className="text-black">{t("about-us.hero.subtitle")}</span>
              </h2>

              <p className="text-xl md:text-2xl mb-12 text-gray-700 leading-relaxed">
                {t("about-us.description")}
              </p>

              <div className="grid grid-cols-2 gap-6 mb-12">
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-orange-500/20">
                  <h3 className="text-2xl mb-2 text-orange-500">
                    {t("about-us.stats.experience.title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("about-us.stats.experience.description")}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-orange-500/20">
                  <h3 className="text-2xl mb-2 text-orange-500">
                    {t("about-us.stats.facilities.title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("about-us.stats.facilities.description")}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-orange-500/20">
                  <h3 className="text-2xl mb-2 text-orange-500">
                    {t("about-us.stats.language.title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("about-us.stats.language.description")}
                  </p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-lg border border-orange-500/20">
                  <h3 className="text-2xl mb-2 text-orange-500">
                    {t("about-us.stats.specialization.title")}
                  </h3>
                  <p className="text-gray-600">
                    {t("about-us.stats.specialization.description")}
                  </p>
                </div>
              </div>

              
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Masterclass Online Introducción - SEGUNDO LUGAR EN EL NUEVO ORDEN */}
      <section
        id="curso-online"
        className="min-h-screen bg-black text-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
                MASTERCLASS
                <br />
                <span className="text-white">ONLINE</span>
                <br />
                <span className="text-3xl md:text-4xl text-orange-300">
                  {t("courses.introduction.title")}
                </span>
              </h2>
              <p className="text-xl md:text-2xl mb-8 text-gray-300 leading-relaxed">
                {t("courses.introduction.description")}
              </p>

              <div className="space-y-4 mb-12">
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature1")}</span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                  <span>
                    {t("courses.introduction.feature2")}
                  </span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                  <span>
                    {t("courses.introduction.feature3")}
                  </span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature4")}</span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature5")}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToCursoOnline}
                >
                  {t("courses.introduction.enrollButton")}
                </Button>
                
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToNuestrosCursos}
                >
                  {t("courses.introduction.viewProgram")}
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="bg-gray-900 border-orange-500/30 p-8 rounded-3xl">
                <ImageWithFallback
                  src="/assets/meristemo.jpeg"
                  alt="Rebel Genomics Lab - Tissue culture masterclass"
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <div className="text-center">
                  <h3 className="text-2xl mb-4 text-orange-400">
                    {t("courses.introduction.limitedGroup")}
                  </h3>
                  <p className="text-gray-400">
                    {t("courses.introduction.nextEdition")}
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. Testimonials Section */}
      <section id="testimonials" className="bg-gradient-to-b from-white to-gray-50 py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-5xl md:text-7xl mb-16 text-center text-orange-500"
          >
            {t("testimonials.title1")}
            <br />
            <span className="text-black">{t("testimonials.title2")}</span>
          </motion.h2>

          {/* Testimonio auto-rotativo */}
          <motion.div
            key={currentTestimonial}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-center px-8 max-w-4xl mx-auto"
          >
            {/* Testimonial Text */}
            <blockquote className="text-2xl md:text-3xl text-gray-800 mb-8 italic leading-relaxed">
              "{testimonials[currentTestimonial].text}"
            </blockquote>

            {/* Profile and Instagram - Side by side */}
            <div className="flex items-center justify-center gap-4">
              <ImageWithFallback
                src={testimonials[currentTestimonial].image}
                alt={t("testimonials.altText")}
                className="w-16 h-16 rounded-full object-cover border-3 border-orange-500"
              />
              <p className="text-orange-500 text-lg">{testimonials[currentTestimonial].instagram}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Masterclass Online Avanzada */}
      <section
        id="masterclass-avanzada"
        className="min-h-screen bg-gradient-to-br from-orange-500 to-red-600 text-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:order-2"
            >
           <h2 className="text-5xl md:text-7xl mb-8 text-black">
                {t("courses.advanced.heroTitle")}
                <br />
                <span className="text-white">{t("courses.advanced.badge")}</span>
                <br />

              </h2>
              <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
                {t("courses.advanced.heroDescription")}
              </p>

              <div className="space-y-4 mb-12">
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-white rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature1")}</span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-white rounded-full mr-4"></div>
                  <span>
                    {t("courses.introduction.feature2")}
                  </span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-white rounded-full mr-4"></div>
                  <span>
                    {t("courses.introduction.feature3")}
                  </span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-white rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature4")}</span>
                </div>
                <div className="flex items-center text-lg">
                  <div className="w-3 h-3 bg-white rounded-full mr-4"></div>
                  <span>{t("courses.introduction.feature5")}</span>
                </div>
              </div>

              
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:order-1"
            >
              <Card className="bg-black/20 backdrop-blur-sm border-white/30 p-8 rounded-3xl">
                <ImageWithFallback
                  src="/assets/nodal.png"
                  alt="Advanced Biotechnology Equipment"
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <div className="text-center">
                  <h3 className="text-2xl mb-4 text-yellow-200">
                    {t("courses.advanced.limitedGroup")}
                  </h3>
                  <p className="text-white/80">
                    {t("courses.advanced.nextEdition")}
                  </p>
                </div>
              </Card>
              
            </motion.div>
            
          </div>
          <div className="flex flex-col sm:flex-row gap-6 mt-4 justify-center">
                <Button
                  size="lg"
                  className="bg-black hover:bg-gray-800 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToCursoAvanzado}
                >
                  {t("courses.advanced.enrollNow")}
                </Button>
                
                <Button
                  size="lg"
                  className="bg-black hover:bg-gray-800 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToNuestrosCursos}
                >
                  {t("cta.compare")}
                </Button>
              </div>
        </div>
      </section>

      {/* Nuestros Clientes Section */}
      <section
        id="nuestros-clientes"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-orange-50 flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              {t("clients.title1")}
              <br />
              <span className="text-black">{t("clients.title2")}</span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
              {t("clients.description")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Cliente 1: Productores de cannabis medicinal */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card 
                className="bg-white border-orange-500/20 shadow-xl rounded-3xl overflow-hidden h-full cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={navigateToNuestrosCursos}
              >
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1567850083672-65ae6c8a696b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwY2FubmFiaXMlMjBwcm9kdWNlciUyMHByb2Zlc3Npb25hbCUyMGdyZWVkZXIlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDF8fHx8fDE3NTgwMDkwOTZ8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt={t("altTexts.cannabisProducer")}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {t("clients.badges.presencial")}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("clients.profiles.cannabisProducers.title")}
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {(t("clients.profiles.cannabisProducers.features", { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>

            {/* Cliente 2: Breeders y genetistas */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card 
                className="bg-white border-orange-500/20 shadow-xl rounded-3xl overflow-hidden h-full cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={navigateToNuestrosCursos}
              >
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1730395727692-2c2aaeddd0b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwbGFudCUyMGJyZWVkZXIlMjBnZW5ldGljaXN0JTIwc2NpZW50aXN0JTIwcG9ydHJhaXR8ZW58MXx8fHwxNzU4MDA5MTAxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt={t("altTexts.plantBreeder")}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {t("clients.badges.onlineComplete")}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("clients.profiles.breeders.title")}
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {(t("clients.profiles.breeders.features", { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>

            {/* Cliente 3: Empresas farmacéuticas */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card 
                className="bg-white border-orange-500/20 shadow-xl rounded-3xl overflow-hidden h-full cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={navigateToNuestrosCursos}
              >
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1685375421073-4dae366f8e84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaGFybWFjZXV0aWNhbCUyMGNvbXBhbnklMjBleGVjdXRpdmUlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDF8fHx8MTc1ODAwOTEwNXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt={t("altTexts.pharmaceuticalExecutive")}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {t("clients.badges.inCompany")}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("clients.profiles.pharmaceutical.title")}
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {(t("clients.profiles.pharmaceutical.features", { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>

            {/* Cliente 5: Perfiles técnicos y autodidactas */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="md:col-span-2 lg:col-span-1"
            >
              <Card 
                className="bg-white border-orange-500/20 shadow-xl rounded-3xl overflow-hidden h-full cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={navigateToNuestrosCursos}
              >
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1562672767-51120ccfdfeb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhZ3JpY3VsdHVyYWwlMjB0ZWNobmljaWFuJTIwZ3Jvd2VyJTIwcG9ydHJhaXQlMjBwcm9mZXNzaW9uYWx8ZW58MXx8fHwxNzU4MDA5MTE0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt={t("altTexts.agriculturalTechnician")}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {t("clients.badges.onlineInitiation")}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("clients.profiles.technical.title")}
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {(t("clients.profiles.technical.features", { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>

            {/* Cliente 6: Startups y microempresas */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <Card 
                className="bg-white border-orange-500/20 shadow-xl rounded-3xl overflow-hidden h-full cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105"
                onClick={navigateToNuestrosCursos}
              >
                <div className="relative">
                  <ImageWithFallback
                    src="https://images.unsplash.com/photo-1611166498484-5585e08d5656?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwc3RhcnR1cCUyMGVudHJlcHJlbmV1ciUyMHBvcnRyYWl0fGVufDF8fHx8MTc1ODAwOTExMHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                    alt={t("altTexts.startupEntrepreneur")}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                      {t("clients.badges.onlineInitiation")}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("clients.profiles.startups.title")}
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    {(t("clients.profiles.startups.features", { returnObjects: true }) as string[]).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            </motion.div>
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h3 className="text-3xl md:text-4xl mb-6 text-black">
              {t("clients.cta.question")}
            </h3>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              {t("clients.cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                onClick={navigateToCursoOnline}
              >
                {t("clients.cta.exploreCourses")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white text-xl px-12 py-6 rounded-full"
              >
                {t("clients.cta.personalizedConsultation")}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Masterclass Presencial */}
      <section
        id="presencial"
        className="min-h-screen bg-white text-black flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
                {t("courses.presencial.heroTitle")}
                <br />
                <span className="text-black">{t("courses.presencial.badge")}</span>
                <br />
                
              </h2>
              <p className="text-xl md:text-2xl mb-8 text-gray-700 leading-relaxed">
                {t("presencial.description")}
              </p>

              <div className="space-y-4 mb-12">
                {(t("presencial.features", { returnObjects: true }) as string[]).map((feature, index) => (
                  <div key={index} className="flex items-center text-lg">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-4"></div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-6">
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToPresencial}
                >
                  {t("presencial.buttons.enrollNow")}
                </Button>
                
                <Button
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-6 rounded-full"
                  onClick={navigateToNuestrosCursos}
                >
                  {t("presencial.buttons.compareCourses")}
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="bg-gray-50 border-orange-500/30 p-8 rounded-3xl shadow-2xl">
                <ImageWithFallback
                  src="/assets/presencial.png"
                  alt="Modern Laboratory Workspace"
                  className="w-full h-80 object-cover rounded-2xl mb-6"
                />
                <div className="text-center">
                  <h3 className="text-2xl mb-4 text-orange-500">
                    {t("courses.presencial.limitedGroup")}
                  </h3>
                  <p className="text-gray-600">
                    {t("courses.presencial.nextEdition")}
                  </p>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contacto"
        className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-orange-900 text-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-6xl md:text-8xl mb-8 text-orange-500">
              {t("contact.title1")}
              <br />
              <span className="text-white">{t("contact.title2")}</span>
            </h2>

            <p className="text-2xl md:text-3xl mb-16 text-gray-300">
              {t("contact.subtitle")}
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/30"
              >
                <h3 className="text-2xl mb-4 text-orange-400">
                  {t("contact.methods.email")}
                </h3>
                <p className="text-lg">
                  hello@rebelgenomics.com
                </p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/30"
              >
                <h3 className="text-2xl mb-4 text-orange-400">
                  {t("contact.methods.phone")}
                </h3>
                <p className="text-lg">09:00 - 16:00 GTM+1</p>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-orange-500/30"
              >
                <h3 className="text-2xl mb-4 text-orange-400">
                  {t("contact.methods.location")}
                </h3>
                <p className="text-lg">{t("contact.location")}</p>
              </motion.div>
            </div>

            <div className="flex flex-wrap gap-6 justify-center">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-16 py-6 rounded-full"
              >
                {t("contact.buttons.requestInfo")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-black hover:bg-white hover:text-black text-xl px-16 py-6 rounded-full"
                onClick={() => window.open("https://calendly.com/pedro-cifuentes-rebelgenomics/30min", "_blank")}
              >
                {t("contact.buttons.scheduleCall")}
              </Button>
            </div>

            {/* Logo at bottom */}
            <motion.div
              className="mt-16"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-20 h-20 mx-auto opacity-60"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}